import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { AttendanceApi, StaffAttendanceRow } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useDefaultYear } from './useDefaultYear';
import { StaffTypeBadge } from './staffTypeBadge';

const columnHelper = createColumnHelper<StaffAttendanceRow>();

const COLUMNS = [
  columnHelper.accessor('personName', { header: 'Name' }),
  columnHelper.accessor('typeLabel', {
    header: 'Type',
    cell: (ctx) => <StaffTypeBadge type={ctx.row.original.type} label={ctx.getValue()} />,
  }),
  columnHelper.accessor('time', { header: 'Time', cell: (ctx) => ctx.getValue() ?? '—' }),
  columnHelper.accessor('remarks', { header: 'Remarks', cell: (ctx) => ctx.getValue() ?? '—' }),
  columnHelper.accessor('addedByName', { header: 'Added By', cell: (ctx) => ctx.getValue() ?? '—' }),
];

export function AdminAttendancePage() {
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'personName', defaultSortOrder: 'asc' });
  const { isLoading: yearLoading, hasAnyData } = useDefaultYear();
  // Never seeded from the academic year's end_date — staff_attendance has no
  // academic_year_id at all, and a year's end_date can sit far beyond any
  // real staff attendance (e.g. 2027-05-31 when the last real row is
  // 2026-06-28), which opens the page empty. Left blank so the API call
  // omits `date` and the BACKEND computes its own "most recent date with
  // data for this source" default (`staff-attendance.service.ts`'s
  // `list()`) — then adopted here once the response confirms which date
  // that was, purely so the date input reflects it.
  const [date, setDate] = useState('');

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['attendance', 'admins', dt.params, date],
    queryFn: () => AttendanceApi.adminList({ ...dt.params, date: date || undefined }),
    enabled: !yearLoading,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!date && pageData?.items.length) setDate(pageData.items[0].date);
  }, [date, pageData]);

  return (
    <>
      <PageHeader title="Admin Attendance" description="Daily attendance for admin/office staff." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name"
            className="pl-9"
          />
        </div>
        <Input type="date" className="!w-40" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>

      {!hasAnyData && !yearLoading ? (
        <div className="card p-8 text-center text-slate-500">No attendance recorded for the selected filters.</div>
      ) : (
        <DataTable
          columns={COLUMNS}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No attendance recorded for the selected filters."
          getRowId={(row) => row.id}
        />
      )}
    </>
  );
}
